// Reusable promise-based confirm dialog (Composition API).
//
//   const { confirm } = useConfirm()
//
//   // simple: resolves true (confirmed) or false (cancelled) — never rejects
//   if (await confirm('Remove this language?')) { ... }
//
//   // with async work: dialog shows a busy spinner while onConfirm runs and
//   // closes when it settles; resolves true when confirmed, false when cancelled
//   await confirm('Close this conversation?', {
//       okText: 'Close',
//       onConfirm: () => api.closeConversation(id),
//   })
//
// Concurrent calls queue and present one after another.
import { reactive } from 'vue'

export const dialogState = reactive({
    open: false,
    message: '',
    okText: 'Continue',
    cancelText: 'Cancel',
    backdropClose: true,
    busy: false,
})

let active = null
const queue = []

const present = (request) => {
    active = request
    Object.assign(dialogState, {
        open: true,
        message: request.message,
        okText: request.options.okText || 'Continue',
        cancelText: request.options.cancelText || 'Cancel',
        backdropClose: request.options.backdropClose !== false,
        busy: false,
    })
}

const dismiss = () => {
    dialogState.open = false
    dialogState.busy = false
    active = null
    if (queue.length) present(queue.shift())
}

export const confirm = (message, options = {}) => new Promise((resolve) => {
    const request = { message, options, resolve }
    if (active) queue.push(request)
    else present(request)
})

// host-component handlers -----------------------------------------------------

export const acceptDialog = async () => {
    if (!active || dialogState.busy) return
    const { resolve, options } = active
    if (typeof options.onConfirm === 'function') {
        dialogState.busy = true
        try {
            await options.onConfirm()
        } finally {
            dismiss()
        }
    } else {
        dismiss()
    }
    resolve(true)
}

export const cancelDialog = () => {
    if (!active || dialogState.busy) return
    const { resolve } = active
    dismiss()
    resolve(false)
}

export const useConfirm = () => ({ confirm })
